import { offline, Offline } from '../core/offline.js';
import { stripCountry } from '../core/country.js';
import { catalog } from '../core/catalog.js';
import { shouldTranscode } from '../core/transcode.js';
import { parseAge } from '../core/age.js';
import { t } from '../core/i18n.js';
import { notify } from '../core/notify.js';
import { escapeHtml, formatBytes, isHlsUrl, pick } from '../core/utils.js';

/* Descargas para ver sin conexión: botón de descarga de fichas y episodios (data-offline), su
   menú de acciones y la reproducción desde el dispositivo. La lógica vive en core/offline.js. */

export function offlineKey(kind, id) {
    return Offline.key(catalog.id, kind, id);
}

export function progressText(rec) {
    if (rec.size) return Math.min(100, Math.floor((rec.received / rec.size) * 100)) + ' %';
    return formatBytes(rec.received);
}

function stateOf(rec) {
    if (!rec) return { status: 'none', icon: 'download', label: t('offline.download') };
    if (rec.status === 'done') return { status: 'done', icon: 'download_done', label: t('offline.downloaded') };
    if (rec.status === 'downloading') return { status: 'downloading', icon: 'downloading', label: progressText(rec) };
    if (rec.status === 'queued') return { status: 'queued', icon: 'schedule', label: t('offline.queued') };
    if (rec.status === 'error') return { status: 'error', icon: 'error_outline', label: t('offline.retry') };
    return { status: 'paused', icon: 'pause_circle', label: rec.waiting ? t('offline.waiting') : t('offline.paused') };
}

/**
 * Botón de descarga. `kind`: 'vod' | 'episode'. Para episodios, `ctx` = { series, season, num }.
 * `icon: true` lo deja solo con el icono (esquina de la ficha en móvil, fila de episodio).
 */
export function offlineButton(kind, id, { cls = 'btn', icon = false, ctx = null } = {}) {
    if (!offline.supported) return '';
    const s = stateOf(offline.get(offlineKey(kind, id)));
    const data = ctx ? ` data-series="${escapeHtml(ctx.series)}" data-season="${Number(ctx.season)}" data-num="${Number(ctx.num)}"` : '';
    return `<button type="button" class="${cls} offline-btn is-${s.status}" data-offline="${kind}:${escapeHtml(id)}"${data} ${icon ? 'data-offline-icon' : ''} aria-label="${escapeHtml(s.status === 'none' ? t('offline.download') : t('offline.title') + ': ' + s.label)}"><span class="mi">${s.icon}</span>${icon ? '' : `<span class="offline-label">${escapeHtml(s.label)}</span>`}</button>`;
}

/** Pone al día los botones ya pintados (estado y porcentaje) sin repintar la página. */
export function refreshOfflineButtons(root = document) {
    root.querySelectorAll('[data-offline]').forEach((btn) => {
        const [kind, id] = btn.getAttribute('data-offline').split(':');
        const s = stateOf(offline.get(offlineKey(kind, id)));
        btn.className = btn.className.replace(/\bis-(none|done|downloading|queued|error|paused)\b/, 'is-' + s.status);
        const icon = btn.querySelector('.mi');
        if (icon && icon.textContent !== s.icon) icon.textContent = s.icon;
        const label = btn.querySelector('.offline-label');
        if (label && label.textContent !== s.label) label.textContent = s.label;
    });
}

const PLAY_FIELDS = ['id', 'name', 'poster', 'backdrop', 'ext', 'year', 'genre', 'rating', 'age', 'plot', 'categoryId', 'durationSecs', 'duration', 'video', 'audio', 'url'];

async function startDownload(kind, id, ctx) {
    if (navigator.onLine === false) return notify.error(t('offline.needNetwork'));
    let rec;
    let codecInfo;
    try {
        if (kind === 'vod') {
            const item = catalog.get('vod', id);
            if (!item) return null;
            const info = (await catalog.vodInfo(id).catch(() => null)) || item;
            if (catalog.ageBlocked(info)) return notify.error(t('age.blockedToast', { label: parseAge(info.age).label }));
            codecInfo = info;
            rec = {
                kind, id: String(id), title: stripCountry(item.name), subtitle: [item.year, String(item.genre || info.genre || '').split(/[,/|;]+/)[0].trim()].filter(Boolean).join(' · '),
                poster: item.poster || info.poster || '', raw: catalog.rawVodUrl(item), ext: item.ext || 'mp4', durationSecs: info.durationSecs || 0,
                play: { item: pick(Object.assign({}, item, info), PLAY_FIELDS) }
            };
        } else {
            const series = await catalog.seriesInfo(ctx.series);
            const ep = series && catalog.findEpisode(series, ctx.season, ctx.num);
            if (!ep) return notify.error(t('common.errorLoad'));
            if (catalog.ageBlocked(series)) return notify.error(t('age.blockedToast', { label: parseAge(series.age).label }));
            codecInfo = ep;
            rec = {
                kind, id: String(ep.id), title: series.name, subtitle: `T${ep.season} E${ep.num}${ep.title ? ' · ' + ep.title : ''}`,
                poster: series.poster || '', seriesId: String(series.id), season: ep.season, num: ep.num,
                raw: catalog.rawEpisodeUrl(ep), ext: ep.ext || 'mp4', durationSecs: ep.durationSecs || 0,
                play: { series: pick(series, PLAY_FIELDS), episode: ep }
            };
        }
        if (!rec.raw || isHlsUrl(rec.raw)) return notify.error(t('offline.error.unsupported'));
        rec.converted = shouldTranscode(codecInfo, rec.raw);
        if (rec.converted) rec.ext = 'mp4';
        rec.key = offlineKey(kind, rec.id);
        rec.profileId = catalog.id;
        rec.viewerId = catalog.viewer ? catalog.viewer.id : '';
        await offline.add(rec);
        notify.toast(t('offline.started'), { icon: 'download', duration: 1800 });
    } catch (e) {
        console.warn('[offline] no se pudo iniciar la descarga', e);
        notify.error(t('offline.error.generic'));
    }
    return rec;
}

/** Reproduce una descarga terminada con los datos guardados: no necesita red ni catálogo. */
export function playOffline(rec) {
    const player = window.IPTV.player;
    const resume = (p) => (p && !p.finished ? p.position || 0 : 0);
    if (rec.kind === 'vod') {
        const item = catalog.get('vod', rec.id) || rec.play.item;
        if (catalog.ageBlocked(rec.play.item)) return notify.error(t('age.blockedToast', { label: parseAge(rec.play.item.age).label }));
        return player.open({ mode: 'vod', item, startPosition: resume(catalog.getProgress('vod', rec.id)) });
    }
    if (catalog.ageBlocked(rec.play.series)) return notify.error(t('age.blockedToast', { label: parseAge(rec.play.series.age).label }));
    const known = catalog.peekInfo('series', rec.seriesId);
    const series = known && known.seasons && known.seasons.length ? known : Object.assign({ seasons: [] }, rec.play.series);
    return player.open({ mode: 'episode', series, episode: rec.play.episode, startPosition: resume(catalog.getProgress('episode', rec.id)) });
}

export function confirmRemove(rec) {
    const downloading = rec.status !== 'done';
    notify.confirm(t(downloading ? 'offline.cancelConfirm' : 'offline.deleteConfirm', { title: rec.title }), t(downloading ? 'offline.cancel' : 'offline.delete'), () => {
        offline.remove(rec.key).catch(() => notify.error(t('offline.error.generic')));
    });
}

export function openOfflineMenu(rec, { showList = true } = {}) {
    const app = window.app;
    const entry = (icon, label, onClick) => ({ text: `<span class="mi">${icon}</span>${label}`, onClick });
    const buttons = [{ text: escapeHtml(rec.title + (rec.kind === 'episode' ? ' · T' + rec.season + ' E' + rec.num : '')), label: true }];
    if (rec.status === 'done') buttons.push(entry('play_arrow', t('offline.play'), () => playOffline(rec)));
    if (rec.status === 'downloading' || rec.status === 'queued') buttons.push(entry('pause', t('offline.pause'), () => offline.pause(rec.key)));
    if (rec.status === 'paused') buttons.push(entry('play_arrow', t('offline.resume'), () => offline.resume(rec.key)));
    if (rec.status === 'error') buttons.push(entry('refresh', t('offline.retry'), () => offline.resume(rec.key)));
    buttons.push(entry('delete', t(rec.status === 'done' ? 'offline.delete' : 'offline.cancel'), () => confirmRemove(rec)));
    if (showList) buttons.push(entry('download', t('offline.seeAll'), () => app.views.main.router.navigate('/offline/')));
    app.actions.create({ cssClass: 'main-menu', buttons: [buttons, [{ text: t('common.cancel'), color: 'red' }]] }).open();
}

/** Empieza la descarga si no existe; si existe, ofrece sus acciones. Para episodios, ctx = { series, season, num }. */
export function offlineAction(kind, id, ctx = null) {
    const rec = offline.get(offlineKey(kind, id));
    if (rec) return openOfflineMenu(rec);
    return startDownload(kind, id, ctx);
}

/** Clic en un botón de descarga (data-offline). */
export function handleOfflineClick(btn) {
    const [kind, id] = btn.getAttribute('data-offline').split(':');
    return offlineAction(kind, id, kind === 'episode' ? { series: btn.getAttribute('data-series'), season: Number(btn.getAttribute('data-season')), num: Number(btn.getAttribute('data-num')) } : null);
}

export function errorText(rec) {
    const key = 'offline.error.' + rec.error;
    const text = t(key);
    return text === key ? t('offline.error.generic') : text;
}
